import { clamp, damp } from "../core/math.js";

export class AudioController {
  #video;
  #store;
  #config;
  #button;
  #context = null;
  #source = null;
  #gain = null;
  #enabled = false;
  #currentGain = 0;
  #scrollVelocity = 0;
  #toggleHandler;

  constructor({ video, store, config, button }) {
    this.#video = video;
    this.#store = store;
    this.#config = config;
    this.#button = button;
    this.#toggleHandler = () => this.toggle();
    this.#button.addEventListener("click", this.#toggleHandler);
  }

  async #unlock() {
    if (this.#context) {
      if (this.#context.state === "suspended") {
        await this.#context.resume();
      }
      return;
    }

    const AudioContextClass =
      window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) {
      throw new Error("Web Audio is unavailable.");
    }

    this.#context = new AudioContextClass();
    this.#source = this.#context.createMediaElementSource(this.#video);
    this.#gain = this.#context.createGain();
    this.#gain.gain.value = 0;
    this.#source.connect(this.#gain).connect(this.#context.destination);
    await this.#context.resume();
  }

  async toggle(force) {
    const nextEnabled =
      typeof force === "boolean" ? force : !this.#enabled;
    if (nextEnabled) {
      try {
        await this.#unlock();
        this.#video.muted = false;
      } catch (error) {
        this.#store.emit("audio:error", { error });
        this.#video.muted = false;
        this.#video.volume = this.#config.baseGain;
      }
    } else if (!this.#context) {
      this.#video.muted = true;
    }

    this.#enabled = nextEnabled;
    this.#button.setAttribute("aria-pressed", String(nextEnabled));
    this.#button.setAttribute(
      "aria-label",
      nextEnabled ? "关闭环境音" : "开启环境音"
    );
    const label = this.#button.querySelector(".audio-toggle__label");
    if (label) {
      label.textContent = nextEnabled ? "SOUND ON" : "SOUND OFF";
    }
    document.body.classList.toggle("audio-on", nextEnabled);
    this.#store.patch(
      {
        audio: {
          enabled: nextEnabled,
          unlocked: Boolean(this.#context)
        }
      },
      "audio:toggle"
    );
  }

  setScrollVelocity(velocity) {
    this.#scrollVelocity = velocity;
  }

  accent(amount = 0.03, duration = 900) {
    if (!this.#enabled) {
      return;
    }
    this.#scrollVelocity += amount * 14000;
    window.setTimeout(() => {
      this.#scrollVelocity *= 0.4;
    }, duration);
  }

  update(deltaSeconds) {
    const scrollFactor = clamp(
      0,
      Math.abs(this.#scrollVelocity) / 1800,
      1
    );
    const target = this.#enabled
      ? Math.min(
          this.#config.maximumGain,
          this.#config.baseGain +
            scrollFactor * this.#config.scrollGainRange
        )
      : 0;
    this.#currentGain = damp(
      this.#currentGain,
      target,
      this.#enabled ? 4.5 : 8,
      deltaSeconds
    );

    if (this.#gain && this.#context) {
      this.#gain.gain.setTargetAtTime(
        this.#currentGain,
        this.#context.currentTime,
        0.08
      );
    } else if (!this.#video.muted) {
      this.#video.volume = clamp(0, this.#currentGain, 1);
    }
    this.#store.patch(
      { audio: { gain: this.#currentGain } },
      "audio:level"
    );
  }

  destroy() {
    this.#button.removeEventListener("click", this.#toggleHandler);
    if (this.#context) {
      this.#context.close().catch(() => {});
    }
    this.#video.muted = true;
  }
}
